import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Task, FullProject, ProjectMetadata } from '../types';
import { useTaskFilterAndSearchEngine } from './appState/useTaskFilterAndSearchEngine';
import { useUrlFilterSync, parseUrlFilterParams } from './useUrlFilterSync';

export const useAppState = (
  tasks: Task[],
  allTasks: Task[],
  currentProjectId?: string,
  allProjects: FullProject[] = [],
  globalMetadata: ProjectMetadata = {} as ProjectMetadata,
  activeWorkspace?: 'professional' | 'private'
) => {
  const [viewMode, setViewMode] = useState<'impactEffort' | 'gantt' | 'list' | 'calendar' | 'concept' | 'conceptBuilder' | 'report' | 'timeline' | 'dashboard' | 'metricsDashboard' | 'changelog' | 'kanban' | 'mindmap' | 'globalKanban' | 'noDateTasks' | 'communication' | 'projectContact' | 'analysis' | 'globalCalendar' | 'aiAssistant' | 'projectPool' | 'globalGantt' | 'projectDocuments'>('globalKanban');
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const saved = localStorage.getItem('gantt_dark_mode');
    if (saved) return saved === 'true';
    return typeof window !== 'undefined' && typeof window.matchMedia === 'function'
      ? window.matchMedia('(prefers-color-scheme: dark)').matches
      : false;
  });

  const [showLegend, setShowLegend] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [showToolsDropdown, setShowToolsDropdown] = useState(false);
  const [showDataDropdown, setShowDataDropdown] = useState(false);
  const [showAIPlanner, setShowAIPlanner] = useState(false);
  const [showProjectManager, setShowProjectManager] = useState(false);
  const [showChatPanel, setShowChatPanel] = useState(false);
  const [activeProjectTab, setActiveProjectTab] = useState<'all' | 'active' | 'archived'>('active');
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [activeWorkshop, setActiveWorkshop] = useState<string>('none');
  const [workshopContext, setWorkshopContext] = useState('');

  const [isReportEditing, setIsReportEditing] = useState(false);
  const [editedReport, setEditedReport] = useState('');
  const [isImporting, setIsImporting] = useState(false);
  const [showResetModal, setShowResetModal] = useState(false);
  const [showProjectOverviewModal, setShowProjectOverviewModal] = useState(false);
  const [conceptBuilderSection, setConceptBuilderSection] = useState<string | null>(null);

  // Smart Adaptive UI: Standard vs. Expert Mode
  const [uiMode, setUiModeRaw] = useState<'standard' | 'expert'>(() => {
    const saved = localStorage.getItem('stratum_ui_mode');
    return saved === 'standard' || saved === 'expert' ? saved : 'standard';
  });
  const setUiMode = useCallback((mode: 'standard' | 'expert') => {
    setUiModeRaw(mode);
    localStorage.setItem('stratum_ui_mode', mode);
  }, []);

  // View-specific sub-states lifted from children
  const [kanbanMode, setKanbanModeRaw] = useState<'status' | 'date' | 'custom'>(() => {
    const saved = localStorage.getItem('gantt_kanban_mode');
    return saved === 'custom' ? 'custom' : 'date';
  });
  const setKanbanMode = useCallback((mode: 'status' | 'date' | 'custom') => {
    setKanbanModeRaw(mode);
    localStorage.setItem('gantt_kanban_mode', mode);
  }, []);

  const [communicationTab, setCommunicationTab] = useState<'board' | 'database' | 'network' | 'archive'>('board');

  const initialUrlFilters = useMemo(
    () => (typeof window !== 'undefined' ? parseUrlFilterParams(window.location.search) : {}),
    []
  );

  const [showDependencies, setShowDependencies] = useState(() => {
    const saved = localStorage.getItem('gantt_show_dependencies');
    return saved ? saved === 'true' : true;
  });
  const [datePadding, setDatePadding] = useState(() => {
    const saved = localStorage.getItem('gantt_date_padding');
    return saved ? parseInt(saved, 10) : 3;
  });
  const [hideEmptyDays, setHideEmptyDays] = useState(() => {
    const saved = localStorage.getItem('gantt_hide_empty_days');
    return saved ? saved === 'true' : false;
  });
  const [showCompleted, setShowCompleted] = useState(() => {
    if (initialUrlFilters.showCompleted !== undefined) return initialUrlFilters.showCompleted;
    const saved = localStorage.getItem('gantt_show_completed');
    return saved ? saved === 'true' : true;
  });
  const [showArchived, setShowArchived] = useState(() => {
    if (initialUrlFilters.showArchived !== undefined) return initialUrlFilters.showArchived;
    const saved = localStorage.getItem('gantt_show_archived');
    return saved ? saved === 'true' : false;
  });
  const [showTaskDetails, setShowTaskDetails] = useState(() => {
    const saved = localStorage.getItem('gantt_show_task_details');
    return saved ? saved === 'true' : true;
  });

  const [searchQuery, setSearchQuery] = useState(() => initialUrlFilters.searchQuery || '');
  const [searchCategory, setSearchCategory] = useState<'all' | 'task' | 'person' | 'inbox' | 'milestone'>('all');
  const [searchIndex, setSearchIndex] = useState(0);
  const [searchMode, setSearchModeRaw] = useState<'filter' | 'highlight'>(() => {
    const saved = localStorage.getItem('gantt_search_mode');
    return saved === 'filter' || saved === 'highlight' ? saved : 'highlight';
  });
  const setSearchMode = useCallback((mode: 'filter' | 'highlight') => {
    setSearchModeRaw(mode);
    localStorage.setItem('gantt_search_mode', mode);
  }, []);
  const [activeSearchTaskId, setActiveSearchTaskId] = useState<string | number | null>(null);
  const [selectedInboxItemId, setSelectedInboxItemId] = useState<string | null>(null);
  const [calendarFilterMode, setCalendarFilterMode] = useState<'all' | 'tasks' | 'milestones' | 'deadlines'>('all');

  const [filterAssignee, setFilterAssignee] = useState(() => {
    return initialUrlFilters.filterAssignee || localStorage.getItem('gantt_filter_assignee') || 'all';
  });
  const [filterType, setFilterType] = useState(() => {
    return initialUrlFilters.filterType || localStorage.getItem('gantt_filter_type') || 'all';
  });
  const [filterStatus, setFilterStatus] = useState(() => {
    return initialUrlFilters.filterStatus || localStorage.getItem('gantt_filter_status') || 'all';
  });
  const [filterPriority, setFilterPriority] = useState(() => {
    return initialUrlFilters.filterPriority || localStorage.getItem('gantt_filter_priority') || 'all';
  });

  useUrlFilterSync({
    searchQuery,
    setSearchQuery,
    filterAssignee,
    setFilterAssignee,
    filterStatus,
    setFilterStatus,
    filterPriority,
    setFilterPriority,
    filterType,
    setFilterType,
    showCompleted,
    setShowCompleted,
    showArchived,
    setShowArchived,
  });

  // Persist settings in localStorage
  useEffect(() => {
    localStorage.setItem('gantt_show_dependencies', String(showDependencies));
  }, [showDependencies]);

  useEffect(() => {
    localStorage.setItem('gantt_date_padding', String(datePadding));
  }, [datePadding]);

  useEffect(() => {
    localStorage.setItem('gantt_hide_empty_days', String(hideEmptyDays));
  }, [hideEmptyDays]);

  useEffect(() => {
    localStorage.setItem('gantt_show_completed', String(showCompleted));
  }, [showCompleted]);

  useEffect(() => {
    localStorage.setItem('gantt_show_archived', String(showArchived));
  }, [showArchived]);

  useEffect(() => {
    localStorage.setItem('gantt_show_task_details', String(showTaskDetails));
  }, [showTaskDetails]);

  useEffect(() => {
    localStorage.setItem('gantt_filter_assignee', filterAssignee);
  }, [filterAssignee]);

  useEffect(() => {
    localStorage.setItem('gantt_filter_type', filterType);
  }, [filterType]);

  useEffect(() => {
    localStorage.setItem('gantt_filter_status', filterStatus);
  }, [filterStatus]);

  useEffect(() => {
    localStorage.setItem('gantt_filter_priority', filterPriority);
  }, [filterPriority]);

  const [editingNameId, setEditingNameId] = useState<string | number | null>(null);
  const [tempName, setTempName] = useState('');
  const [editingTask, setEditingTask] = useState<Task | null>(null);
  const [depSearchTerm, setDepSearchTerm] = useState('');
  const [infoTask, setInfoTask] = useState<Task | null>(null);

  const [hoveredTaskInfo, setHoveredTaskInfo] = useState<{ task: Task; x: number; y: number } | null>(null);
  const hoverTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [isNearLeftEdge, setIsNearLeftEdge] = useState(false);
  const [isNearRightEdge, setIsNearRightEdge] = useState(false);

  const [projectToDuplicate, setProjectToDuplicate] = useState<string | null>(null);
  const [projectToDelete, setProjectToDelete] = useState<string | null>(null);
  const [projectToRename, setProjectToRename] = useState<string | null>(null);
  const [projectToShare, setProjectToShare] = useState<string | null>(null);
  const [isArchiveModalOpen, setIsArchiveModalOpen] = useState(false);

  const [calendarDate, setCalendarDate] = useState(new Date());
  const [calendarViewType, setCalendarViewType] = useState<'month' | 'week'>('month');

  // Sync dark mode
  useEffect(() => {
    if (isDarkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
    localStorage.setItem('gantt_dark_mode', isDarkMode.toString());
  }, [isDarkMode]);

  const {
    visibleTasks,
    visibleTasksRef,
    searchResults,
    searchMatchIds,
    activeSearchMatchId,
    allAssignees,
  } = useTaskFilterAndSearchEngine({
    tasks,
    allTasks,
    currentProjectId,
    allProjects,
    globalMetadata,
    activeWorkspace,
    viewMode,
    showCompleted,
    showArchived,
    filterAssignee,
    filterType,
    filterStatus,
    filterPriority,
    searchQuery,
    searchMode,
    searchCategory,
    activeSearchTaskId,
    setActiveSearchTaskId,
    setSearchIndex,
  });

  const handleTaskHover = useCallback((task: Task | null, x: number, y: number) => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }

    if (!task) {
      setHoveredTaskInfo(prev => (prev === null ? null : null));
      return;
    }

    hoverTimeoutRef.current = setTimeout(() => {
      setHoveredTaskInfo({ task, x, y });
    }, 600);
  }, []);

  const handleBrainstorm = useCallback((topic: string, context?: string) => {
    setActiveWorkshop(topic);
    setWorkshopContext(context || '');
    setShowChatPanel(true);
  }, []);

  return useMemo(() => ({
    viewMode, setViewMode,
    isDarkMode, setIsDarkMode,
    showLegend, setShowLegend,
    showFilters, setShowFilters,
    showToolsDropdown, setShowToolsDropdown,
    showDataDropdown, setShowDataDropdown,
    showAIPlanner, setShowAIPlanner,
    showProjectManager, setShowProjectManager,
    showChatPanel, setShowChatPanel,
    activeProjectTab, setActiveProjectTab,
    isSidebarCollapsed, setIsSidebarCollapsed,
    activeWorkshop, setActiveWorkshop,
    workshopContext, setWorkshopContext,
    isReportEditing, setIsReportEditing,
    editedReport, setEditedReport,
    isImporting, setIsImporting,
    showResetModal, setShowResetModal,
    showProjectOverviewModal, setShowProjectOverviewModal,
    showDependencies, setShowDependencies,
    datePadding, setDatePadding,
    hideEmptyDays, setHideEmptyDays,
    showCompleted, setShowCompleted,
    showArchived, setShowArchived,
    showTaskDetails, setShowTaskDetails,
    searchQuery, setSearchQuery,
    searchMode, setSearchMode,
    searchCategory, setSearchCategory,
    searchIndex, setSearchIndex,
    activeSearchTaskId, setActiveSearchTaskId,
    activeSearchMatchId,
    filterAssignee, setFilterAssignee,
    filterType, setFilterType,
    filterStatus, setFilterStatus,
    filterPriority, setFilterPriority,
    editingNameId, setEditingNameId,
    tempName, setTempName,
    editingTask, setEditingTask,
    depSearchTerm, setDepSearchTerm,
    infoTask, setInfoTask,
    hoveredTaskInfo, setHoveredTaskInfo,
    isNearLeftEdge, setIsNearLeftEdge,
    isNearRightEdge, setIsNearRightEdge,
    calendarDate, setCalendarDate,
    calendarViewType, setCalendarViewType,
    projectToDuplicate, setProjectToDuplicate,
    projectToDelete, setProjectToDelete,
    projectToRename, setProjectToRename,
    projectToShare, setProjectToShare,
    isArchiveModalOpen, setIsArchiveModalOpen,
    visibleTasks,
    visibleTasksRef,
    searchResults,
    searchMatchIds,
    allAssignees,
    handleTaskHover,
    handleBrainstorm,
    kanbanMode, setKanbanMode,
    communicationTab, setCommunicationTab,
    selectedInboxItemId, setSelectedInboxItemId,
    calendarFilterMode, setCalendarFilterMode,
    conceptBuilderSection, setConceptBuilderSection,
    uiMode, setUiMode
  }), [
    viewMode, isDarkMode, showLegend, showFilters, showToolsDropdown, showDataDropdown,
    showAIPlanner, showProjectManager, showChatPanel, activeProjectTab, isSidebarCollapsed,
    activeWorkshop, workshopContext, isReportEditing, editedReport, isImporting,
    showResetModal, showProjectOverviewModal,
    showDependencies, datePadding, hideEmptyDays, showCompleted, showArchived,
    showTaskDetails, searchQuery, searchMode, setSearchMode, searchCategory, searchIndex,
    activeSearchTaskId, activeSearchMatchId, filterAssignee, filterType, filterStatus,
    filterPriority, editingNameId, tempName, editingTask, depSearchTerm, infoTask,
    hoveredTaskInfo, isNearLeftEdge, isNearRightEdge, calendarDate, calendarViewType,
    projectToDuplicate, projectToDelete, projectToRename, projectToShare,
    isArchiveModalOpen, visibleTasks, visibleTasksRef, searchResults, searchMatchIds, allAssignees,
    handleTaskHover, handleBrainstorm, kanbanMode, setKanbanMode, communicationTab,
    selectedInboxItemId, calendarFilterMode, conceptBuilderSection, uiMode, setUiMode
  ]);
};
